import { motion, AnimatePresence } from "motion/react";
import { X, Download, Printer, Share2, Droplets, Thermometer, Weight } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";

interface SlumpReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: {
    batchId: string;
    grade: string;
    plant: string;
    timestamp: string;
    slumpValue: number;
    slumpUnit: string;
    slumpRange: string;
    slumpStatus: string;
    slumpImage: string;
    temperature: number;
    density: number;
    wcRatio: number;
  };
}

export default function SlumpReportModal({
  isOpen,
  onClose,
  data,
}: SlumpReportModalProps) {
  const handleDownload = () => {
    console.log("Downloading slump report PDF...");
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    console.log("Sharing report...");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Bottom Sheet Modal */}
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center md:p-4 pointer-events-none pb-16 md:pb-0">
            <motion.div
              initial={{ opacity: 0, y: "100%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "100%" }}
              transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
              className="bg-white rounded-t-3xl md:rounded-2xl w-full md:max-w-3xl max-h-[85vh] md:max-h-[90vh] overflow-hidden pointer-events-auto flex flex-col"
              style={{ boxShadow: "0 -10px 40px rgba(0, 0, 0, 0.15), 0 20px 50px rgba(0, 0, 0, 0.3)" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag Handle (Mobile Only) */}
              <div className="md:hidden flex justify-center py-3 bg-white">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
                <div>
                  <h2 className="text-slate-900" style={{ fontSize: "20px", fontWeight: "700" }}>
                    Slump Test Report
                  </h2>
                  <p className="text-slate-500" style={{ fontSize: "14px" }}>
                    {data.batchId} • {data.timestamp}
                  </p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </motion.button>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto flex-1" style={{ paddingBottom: "80px" }}>
                <div className="p-6 space-y-6">
                  {/* Test Summary */}
                  <div>
                    <h3 className="text-slate-900 mb-4" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Test Summary
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-slate-500 mb-2" style={{ fontSize: "12px", fontWeight: "600" }}>
                          Batch ID
                        </div>
                        <div className="text-slate-900" style={{ fontSize: "16px", fontWeight: "700" }}>
                          {data.batchId}
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-slate-500 mb-2" style={{ fontSize: "12px", fontWeight: "600" }}>
                          Concrete Grade
                        </div>
                        <div className="text-slate-900" style={{ fontSize: "16px", fontWeight: "700" }}>
                          {data.grade}
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-slate-500 mb-2" style={{ fontSize: "12px", fontWeight: "600" }}>
                          Plant Location
                        </div>
                        <div className="text-slate-900" style={{ fontSize: "14px", fontWeight: "600" }}>
                          {data.plant}
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-slate-500 mb-2" style={{ fontSize: "12px", fontWeight: "600" }}>
                          Test Time
                        </div>
                        <div className="text-slate-900" style={{ fontSize: "14px", fontWeight: "600" }}>
                          {data.timestamp}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Slump Results */}
                  <div>
                    <h3 className="text-slate-900 mb-4" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Slump Measurement
                    </h3>
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-green-500 rounded-xl">
                          <Droplets className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <div className="text-green-900" style={{ fontSize: "14px", fontWeight: "600" }}>
                            Workability Status
                          </div>
                          <div className="text-green-600" style={{ fontSize: "12px" }}>
                            {data.slumpStatus}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-green-700 mb-1" style={{ fontSize: "12px", fontWeight: "600" }}>
                            Measured Slump
                          </div>
                          <div className="text-green-900" style={{ fontSize: "32px", fontWeight: "700", letterSpacing: "-0.02em" }}>
                            {data.slumpValue}
                            <span style={{ fontSize: "18px", fontWeight: "600" }} className="text-green-700 ml-2">
                              {data.slumpUnit}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-green-700 mb-1" style={{ fontSize: "12px", fontWeight: "600" }}>
                            Target Range
                          </div>
                          <div className="text-green-900" style={{ fontSize: "24px", fontWeight: "700" }}>
                            {data.slumpRange}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Parameters */}
                  <div>
                    <h3 className="text-slate-900 mb-4" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Additional Parameters
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Thermometer className="w-4 h-4 text-orange-500" />
                          <div className="text-slate-500" style={{ fontSize: "12px", fontWeight: "600" }}>
                            Temperature
                          </div>
                        </div>
                        <div className="text-slate-900" style={{ fontSize: "24px", fontWeight: "700" }}>
                          {data.temperature}°C
                        </div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Weight className="w-4 h-4 text-blue-500" />
                          <div className="text-slate-500" style={{ fontSize: "12px", fontWeight: "600" }}>
                            Density
                          </div>
                        </div>
                        <div className="text-slate-900" style={{ fontSize: "24px", fontWeight: "700" }}>
                          {data.density}
                        </div>
                        <div className="text-slate-500" style={{ fontSize: "12px" }}>kg/m³</div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Droplets className="w-4 h-4 text-cyan-500" />
                          <div className="text-slate-500" style={{ fontSize: "12px", fontWeight: "600" }}>
                            W/C Ratio
                          </div>
                        </div>
                        <div className="text-slate-900" style={{ fontSize: "24px", fontWeight: "700" }}>
                          {data.wcRatio}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Test Image */}
                  <div>
                    <h3 className="text-slate-900 mb-4" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Test Documentation
                    </h3>
                    <ImageWithFallback
                      src={data.slumpImage}
                      alt="Slump test"
                      className="w-full h-80 object-cover rounded-xl border border-slate-200"
                    />
                  </div>

                  {/* Compliance Note */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="text-blue-900 mb-1" style={{ fontWeight: "600" }}>
                      Compliance Note
                    </div>
                    <p className="text-blue-700" style={{ fontSize: "14px" }}>
                      This slump test was conducted in accordance with IS 1199:1959 standards. 
                      The measured slump value falls within the acceptable range for {data.grade} grade concrete, 
                      indicating optimal workability for placement and compaction.
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="p-5 border-t border-slate-100 flex-shrink-0 bg-white">
                <div className="grid grid-cols-3 gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleShare}
                    className="py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-all font-semibold flex items-center justify-center gap-2"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handlePrint}
                    className="py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-all font-semibold flex items-center justify-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Print
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleDownload}
                    className="py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-all font-semibold flex items-center justify-center gap-2 shadow-lg shadow-green-500/30"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}